/**
 * CompanyReviewsPage — read-only list of customer ratings + reviews
 * for the company. Mirrors Flutter `company/company_reviews_page.dart`.
 *
 *   Server: GET /api/cargo/me/company/reviews
 *   Visible to company members (not just owner). Server enforces role.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Star, MessageSquare, Loader, Hash } from 'lucide-react';
import { useMyCompany, useCompanyReviews } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import type { CompanyReview } from '@/types/companyExtras';

export default function CompanyReviewsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: company, isLoading: companyLoading } = useMyCompany();
  const { data: reviews, isLoading } = useCompanyReviews();

  if (companyLoading || isLoading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!company) {
    navigate('/company', { replace: true });
    return null;
  }

  const total = reviews?.length || 0;
  const avg = total > 0 ? reviews!.reduce((s, r) => s + r.rating, 0) / total : 0;

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Star size={20} /> {t('reviews_title', 'التقييمات')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('reviews_subtitle', 'تقييمات الزبائن الذين تعاملوا مع شركتك.')}
        </p>

        {/* Summary card */}
        {total > 0 && (
          <div className="bg-surface border border-border rounded-2xl p-5 mb-4 flex items-center gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{avg.toFixed(2)}</div>
              <Stars value={avg} />
            </div>
            <div className="border-r border-border h-12 mx-2" />
            <div>
              <div className="text-2xl font-bold text-text-primary">{total}</div>
              <div className="text-xs text-text-secondary">{t('reviews_count', 'تقييم')}</div>
            </div>
          </div>
        )}

        {total === 0 ? (
          <div className="bg-surface border border-border rounded-2xl p-10 text-center">
            <MessageSquare size={48} className="mx-auto text-text-tertiary mb-3" />
            <p className="text-text-secondary">
              {t('reviews_empty', 'لا توجد تقييمات بعد.')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {reviews?.map((r) => <ReviewCard key={r.id} review={r} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function ReviewCard({ review }: { review: CompanyReview }) {
  const { t } = useTranslation();
  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-bold text-text-primary">
            {review.reviewer_name || t('anonymous', 'مستخدم')}
          </div>
          <div className="text-xs text-text-tertiary">
            {new Date(review.created_at).toLocaleDateString('ar-SY')}
          </div>
        </div>
        <Stars value={review.rating} />
      </div>
      {review.comment && (
        <p className="text-sm text-text-primary leading-relaxed mt-2">{review.comment}</p>
      )}
      {review.shipment_public_code && (
        <div className="flex items-center gap-1 text-xs text-text-tertiary mt-2">
          <Hash size={12} /> {review.shipment_public_code}
        </div>
      )}
    </div>
  );
}

function Stars({ value }: { value: number }) {
  return (
    <div className="inline-flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          className={n <= Math.round(value) ? 'text-warning fill-warning' : 'text-border'}
        />
      ))}
    </div>
  );
}
